import { PERMISSION_KEYS } from 'constants-permissions';
import { safeConvertToUUID } from 'uuid-encoding';
import { ALL_ACTIONS, READ_ONLY_ACTIONS } from '../Action';
import {
  hasAnyPermission,
  isAssigneeOrTeamMember,
  isOwner,
  isSuperUser,
  type Predicate,
} from '../predicates';
import type { ServiceRequestResource } from '../Resource';
import { evaluateRules, type AccessRequest, type Rule } from '../Rule';

const hasAnyCompanyPermission =
  (keys: string[]): Predicate<ServiceRequestResource> =>
  (actor, resource) =>
    actor.hasPermission(keys, safeConvertToUUID(resource.companyId));

export const SERVICE_REQUEST_RULES: readonly Rule<ServiceRequestResource>[] = [
  { name: 'superuser', actions: ALL_ACTIONS, appliesTo: isSuperUser },
  {
    name: 'admin',
    actions: ALL_ACTIONS,
    appliesTo: hasAnyPermission([PERMISSION_KEYS.PERMISSION_ADMIN]),
  },
  { name: 'owner', actions: ALL_ACTIONS, appliesTo: isOwner },
  {
    name: 'attach-file',
    actions: ALL_ACTIONS,
    appliesTo: hasAnyPermission([
      PERMISSION_KEYS.PERMISSION_WORK_ORDERS_SERVICE_REQUEST_ATTACH_FILE,
    ]),
  },
  {
    name: 'company-invoices',
    actions: READ_ONLY_ACTIONS,
    appliesTo: hasAnyCompanyPermission([
      PERMISSION_KEYS.PERMISSION_COMPANY_INVOICES,
    ]),
  },
  {
    name: 'view-all',
    actions: READ_ONLY_ACTIONS,
    appliesTo: hasAnyPermission([
      PERMISSION_KEYS.PERMISSION_WORK_ORDERS_SERVICE_REQUEST_VIEW,
    ]),
  },
  {
    name: 'assignee-team',
    actions: READ_ONLY_ACTIONS,
    appliesTo: isAssigneeOrTeamMember,
  },
];

const COMPANY_EDITOR_RULES: readonly Rule<ServiceRequestResource>[] = [
  {
    name: 'view-edit-company',
    actions: ALL_ACTIONS,
    appliesTo: hasAnyCompanyPermission([
      PERMISSION_KEYS.PERMISSION_WORK_ORDERS_SERVICE_REQUEST_VIEW_EDIT_COMPANY,
    ]),
  },
];

interface ServiceRequestAccessOptions {
  companyRoleEnabled?: boolean;
}

export const canAccessServiceRequestAttachment = (
  request: AccessRequest<ServiceRequestResource>,
  { companyRoleEnabled = false }: ServiceRequestAccessOptions = {}
): boolean =>
  evaluateRules(SERVICE_REQUEST_RULES, request) ||
  (companyRoleEnabled && evaluateRules(COMPANY_EDITOR_RULES, request));
